import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FavoriteIcon from "@mui/icons-material/Favorite";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api/client";
import { INCIDENT_TYPES, SERVICE_OPTIONS } from "../constants/incidentOptions";

// Format incident type for display: "assist-motorist" -> "ASSIST MOTORIST"
const formatIncidentType = (type) => {
  if (!type) return "—";
  return type.replace(/-/g, ' ').toUpperCase();
};

const formatDuration = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return "0m";
  }

  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const remaining = rounded % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (remaining > 0 || parts.length === 0) {
    parts.push(`${remaining}m`);
  }

  return parts.join(" ");
};

const computeDurationMinutes = (start, end) => {
  if (!start || !end) {
    return null;
  }

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }

  const diffMinutes = (endDate.getTime() - startDate.getTime()) / 60000;
  return diffMinutes > 0 ? diffMinutes : null;
};

const toLocalInputValue = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const pad = (input) => String(input).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseLocalInput = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
};

const formatTimestamp = (value) => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

const APPLICATION_CERTIFICATION_LABELS = {
  cpr_first_aid: "CPR/First Aid Certified",
  emt: "EMT/Paramedic",
  firefighter: "TCFD Firefighter License",
  tcole: "TCOLE Law Enforcement License",
  cdl: "Commercial Driver's License (CDL)",
  ham_radio: "Ham Radio License",
  wrecker_operator: "TDLR Licensed Wrecker Operator",
  responder_safety_learning_network: "Responder Safety Learning Network",
  nims: "National Incident Management System (NIMS)",
  ics: "Incident Command System",
};

const APPLICATION_EXPERIENCE_LABELS = {
  roadside_assistance: "Roadside Assistance Experience",
  towing: "Towing Experience",
  ems_fire: "EMS/Fire Department Experience",
  law_enforcement: "Law Enforcement Experience",
  military: "Military Experience",
  mechanic: "Mechanic Experience",
  other_emergency: "Other Emergency Services Experience",
};

const APPLICATION_EQUIPMENT_LABELS = {
  jump_starter: "Jump Starter/Battery Pack",
  jumper_cables: "Jumper Cables",
  tire_inflator: "Tire Inflator/Air Compressor",
  lug_wrench: "Lug Wrench",
  hydraulic_jack: "Hydraulic Jack",
  tire_plug_kit: "Tire Plug Kit",
  tow_straps: "Tow Straps/Recovery Straps",
  gas_can: "Gas Can",
  safety_cones: "Safety Cones/Triangles",
  emergency_warning_lights: "Emergency Warning Lights",
  safety_vest: "Reflective Safety Vest",
  flashlight: "High-Power Flashlight",
  first_aid_kit: "First Aid Kit",
  fire_extinguisher: "Fire Extinguisher",
  basic_tools: "Basic Hand Tools",
};

const APPLICATION_FREQUENCY_LABELS = {
  few_times_week: "A few times per week",
  weekends: "Weekends only",
  few_times_month: "A few times per month",
  occasionally: "Occasionally, when available",
};

const APPLICATION_VEHICLE_TYPE_LABELS = {
  sedan: "Sedan",
  suv: "SUV",
  truck: "Pickup Truck",
  van: "Van",
  other: "Other",
};

const formatApplicationCertifications = (value) => {
  if (!value || typeof value !== "string") {
    return "None";
  }

  const normalizedValues = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => APPLICATION_CERTIFICATION_LABELS[item] || item);

  return normalizedValues.length > 0 ? normalizedValues.join(", ") : "None";
};

function Supervisor() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedLogs, setApprovedLogs] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState(false);
  const [approvalMore, setApprovalMore] = useState(false);
  const [loadingPending, setLoadingPending] = useState(false);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [pendingError, setPendingError] = useState("");
  const [approvedError, setApprovedError] = useState("");
  const [alert, setAlert] = useState(null);
  const [integrityItems, setIntegrityItems] = useState([]);
  const [integrityLoading, setIntegrityLoading] = useState(false);
  const [integrityError, setIntegrityError] = useState("");
  const [integrityBusy, setIntegrityBusy] = useState({});
  const [editDialog, setEditDialog] = useState({ open: false, incident: null, start: "", end: "", submitting: false, error: "" });
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null, submitting: false, error: "" });

  // Applications state
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationsError, setApplicationsError] = useState("");
  const [applicationDetailDialog, setApplicationDetailDialog] = useState({ open: false, application: null, loading: false });
  const [applicationReviewDialog, setApplicationReviewDialog] = useState({ open: false, application: null, action: "", notes: "", submitting: false, error: "" });
  const [applicationFilter, setApplicationFilter] = useState("pending");
  const [applicationStats, setApplicationStats] = useState({ pending: 0, approved: 0, denied: 0, total: 0 });

  // Donation Stories state
  const [donationStories, setDonationStories] = useState([]);
  const [donationStoriesLoading, setDonationStoriesLoading] = useState(false);
  const [donationStoriesError, setDonationStoriesError] = useState("");
  const [donationStats, setDonationStats] = useState({ total: 0, unread: 0, archived: 0 });
  const [donationStoryDetailDialog, setDonationStoryDetailDialog] = useState({ open: false, story: null });
  const [showArchivedDonations, setShowArchivedDonations] = useState(false);

  const hasRequests = useMemo(() => pendingRequests.length > 0, [pendingRequests]);
  const hasApprovedLogs = useMemo(() => approvedLogs.length > 0, [approvedLogs]);
  const hasApplications = useMemo(() => applications.length > 0, [applications]);
  const hasDonationStories = useMemo(() => donationStories.length > 0, [donationStories]);
  const integrityRows = useMemo(() => {
    return integrityItems
      .map((item) => {
        const existingDuration = Number(item.durationMinutes);
        const durationMinutes = Number.isFinite(existingDuration)
          ? existingDuration
          : computeDurationMinutes(item.occurredAtUtc, item.occurredEndUtc);
        return {
          ...item,
          durationMinutes,
        };
      })
      .sort((a, b) => (Number.isFinite(b.durationMinutes) ? b.durationMinutes : 0) - (Number.isFinite(a.durationMinutes) ? a.durationMinutes : 0));
  }, [integrityItems]);
  const hasIntegrityItems = useMemo(() => integrityRows.length > 0, [integrityRows]);
  const proposedDuration = useMemo(() => {
    if (!editDialog.open || !editDialog.start || !editDialog.end) {
      return null;
    }

    return computeDurationMinutes(editDialog.start, editDialog.end);
  }, [editDialog.end, editDialog.open, editDialog.start]);
  const currentIncidentDuration = useMemo(() => {
    if (!editDialog.incident) {
      return null;
    }

    const value = Number(editDialog.incident.durationMinutes);
    if (Number.isFinite(value)) {
      return value;
    }

    return computeDurationMinutes(editDialog.incident.occurredAtUtc, editDialog.incident.occurredEndUtc);
  }, [editDialog.incident]);

  const fetchPendingRequests = useCallback(async () => {
    setLoadingPending(true);
    setPendingError("");

    try {
      const response = await api.get("/video-bypass/requests/pending");
      const items = Array.isArray(response.data) ? response.data : [];
      setPendingRequests(items);
    } catch (err) {
      console.error(err);
      setPendingError("Unable to load pending bypass approvals right now.");
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const fetchApprovedLogs = useCallback(async (older = false, append = false) => {
    setLoadingApproved(true);
    setApprovedError("");

    try {
      const response = await api.get("/video-bypass/requests/approved", { params: { limit: older ? 20 : 10, recentOnly: !older, ...(typeof append === "number" ? { beforeId: append } : {}) } });
      const items = Array.isArray(response.data) ? response.data : [];
      setApprovedLogs(old => typeof append === "number" ? [...old, ...items] : items);
      setApprovalHistory(older); setApprovalMore(items.length === (older ? 20 : 10));
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404) {
        // Older API builds may not expose the approved endpoint yet. Treat as "no data".
        setApprovedLogs([]);
      } else {
        setApprovedError("Unable to load previously approved bypasses right now.");
      }
    } finally {
      setLoadingApproved(false);
    }
  }, []);

  const fetchIntegrityItems = useCallback(async () => {
    setIntegrityLoading(true);
    setIntegrityError("");

    try {
      const response = await api.get("/incidents/duration-review/pending");
      const items = Array.isArray(response.data) ? response.data : [];
      setIntegrityItems(items);
    } catch (err) {
      console.error(err);
      setIntegrityError("Unable to load incidents requiring duration review.");
    } finally {
      setIntegrityLoading(false);
    }
  }, []);

  const fetchApplications = useCallback(async (status = applicationFilter) => {
    setApplicationsLoading(true);
    setApplicationsError("");

    try {
      const response = await api.get("/applications", { params: status !== "all" ? { status } : {} });
      const items = Array.isArray(response.data) ? response.data : [];
      setApplications(items);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404) {
        setApplications([]);
      } else {
        setApplicationsError("Unable to load applications right now.");
      }
    } finally {
      setApplicationsLoading(false);
    }
  }, [applicationFilter]);

  const fetchApplicationStats = useCallback(async () => {
    try {
      const response = await api.get("/applications/stats");
      setApplicationStats(response.data);
    } catch (err) {
      console.error("Failed to fetch application stats:", err);
    }
  }, []);

  const fetchDonationStories = useCallback(async (includeArchived = showArchivedDonations) => {
    setDonationStoriesLoading(true);
    setDonationStoriesError("");
    try {
      const response = await api.get("/donation-stories", { params: { includeArchived } });
      const items = Array.isArray(response.data) ? response.data : [];
      setDonationStories(items);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 404) {
        setDonationStories([]);
      } else {
        setDonationStoriesError("Unable to load donation stories right now.");
      }
    } finally {
      setDonationStoriesLoading(false);
    }
  }, [showArchivedDonations]);

  const fetchDonationStats = useCallback(async () => {
    try {
      const response = await api.get("/donation-stories/stats");
      setDonationStats(response.data);
    } catch (err) {
      console.error("Failed to fetch donation stats:", err);
    }
  }, []);

  const fetchApplicationDetail = useCallback(async (id) => {
    setApplicationDetailDialog((prev) => ({ ...prev, loading: true }));
    try {
      const response = await api.get(`/applications/${id}`);
      setApplicationDetailDialog({ open: true, application: response.data, loading: false });
    } catch (err) {
      console.error(err);
      setAlert({ type: "error", message: "Unable to load application details." });
      setApplicationDetailDialog({ open: false, application: null, loading: false });
    }
  }, []);

  useEffect(() => {
    fetchPendingRequests();
    fetchApprovedLogs();
    fetchIntegrityItems();
    fetchApplications();
    fetchApplicationStats();
    fetchDonationStories();
    fetchDonationStats();
  }, [fetchPendingRequests, fetchApprovedLogs, fetchIntegrityItems, fetchApplications, fetchApplicationStats, fetchDonationStories, fetchDonationStats]);

  const closeEditDialog = () => {
    setEditDialog({ open: false, incident: null, start: "", end: "", submitting: false, error: "" });
  };

  const openEditDialog = (incident) => {
    setEditDialog({
      open: true,
      incident,
      start: toLocalInputValue(incident.occurredAtUtc),
      end: toLocalInputValue(incident.occurredEndUtc),
      submitting: false,
      error: "",
    });
  };

  const handleEditFieldChange = (field) => (event) => {
    const { value } = event.target;
    setEditDialog((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSubmit = async () => {
    const incident = editDialog.incident;
    if (!incident || editDialog.submitting) {
      return;
    }

    const startIso = parseLocalInput(editDialog.start);
    const endIso = parseLocalInput(editDialog.end);

    if (!startIso || !endIso) {
      setEditDialog((prev) => ({ ...prev, error: "Provide valid start and end times." }));
      return;
    }

    if (new Date(endIso) <= new Date(startIso)) {
      setEditDialog((prev) => ({ ...prev, error: "End time must be after start time." }));
      return;
    }

    const payload = {};
    const originalStart = new Date(incident.occurredAtUtc);
    const originalEnd = incident.occurredEndUtc ? new Date(incident.occurredEndUtc) : null;
    const newStart = new Date(startIso);
    const newEnd = new Date(endIso);

    if (Number.isNaN(newStart.getTime())) {
      setEditDialog((prev) => ({ ...prev, error: "Provide a valid start time." }));
      return;
    }

    if (Number.isNaN(newEnd.getTime())) {
      setEditDialog((prev) => ({ ...prev, error: "Provide a valid end time." }));
      return;
    }

    if (Number.isNaN(originalStart.getTime()) || originalStart.getTime() !== newStart.getTime()) {
      payload.occurredAtUtc = startIso;
    }

    if (!originalEnd || Number.isNaN(originalEnd.getTime()) || originalEnd.getTime() !== newEnd.getTime()) {
      payload.occurredEndUtc = endIso;
    }

    if (Object.keys(payload).length === 0) {
      setEditDialog((prev) => ({ ...prev, error: "No changes detected." }));
      return;
    }

    setEditDialog((prev) => ({ ...prev, submitting: true, error: "" }));

    try {
      await api.put(`/incidents/${incident.id}`, payload);
      setAlert({ type: "success", message: `Updated call times for CAD ${incident.cadId}.` });
      closeEditDialog();
      await fetchIntegrityItems();
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to update call times right now.";
      setEditDialog((prev) => ({
        ...prev,
        submitting: false,
        error: typeof message === "string" ? message : "Unable to update call times right now.",
      }));
    }
  };

  const handleApproveDuration = async (incident) => {
    if (!incident) {
      return;
    }

    setIntegrityBusy((prev) => ({ ...prev, [incident.id]: true }));

    try {
      await api.post(`/incidents/${incident.id}/duration-review/approve`);
      setAlert({ type: "success", message: `Approved duration for CAD ${incident.cadId}.` });
      await fetchIntegrityItems();
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to approve this incident right now.";
      setAlert({
        type: "error",
        message: typeof message === "string" ? message : "Unable to approve this incident right now.",
      });
    } finally {
      setIntegrityBusy((prev) => {
        const next = { ...prev };
        delete next[incident.id];
        return next;
      });
    }
  };

  const closeApproveDialog = () => {
    setApproveDialog({ open: false, request: null, submitting: false, error: "" });
  };

  const openApproveConfirmation = (request) => {
    setApproveDialog({ open: true, request, submitting: false, error: "" });
  };

  const handleApprove = async () => {
    if (!approveDialog.request || approveDialog.submitting) {
      return;
    }

    setApproveDialog((prev) => ({ ...prev, submitting: true, error: "" }));

    try {
      await api.post(`/video-bypass/requests/${approveDialog.request.id}/approve`);
      setPendingRequests((prev) => prev.filter((item) => item.id !== approveDialog.request.id));
      setAlert({ type: "success", message: `Approved bypass for CAD ${approveDialog.request.cadId}.` });
      await fetchApprovedLogs();
      closeApproveDialog();
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to approve this bypass request right now.";
      setApproveDialog((prev) => ({
        ...prev,
        submitting: false,
        error: typeof message === "string" ? message : "Unable to approve this bypass request right now.",
      }));
    }
  };

  // Application handlers
  const handleApplicationFilterChange = (event, newValue) => {
    setApplicationFilter(newValue);
    fetchApplications(newValue);
  };

  const openApplicationDetail = (application) => {
    fetchApplicationDetail(application.id);
  };

  const closeApplicationDetailDialog = () => {
    setApplicationDetailDialog({ open: false, application: null, loading: false });
  };

  const openApplicationReview = (application, action) => {
    setApplicationReviewDialog({ open: true, application, action, notes: "", submitting: false, error: "" });
  };

  const closeApplicationReviewDialog = () => {
    setApplicationReviewDialog({ open: false, application: null, action: "", notes: "", submitting: false, error: "" });
  };

  const handleApplicationReview = async () => {
    const { application, action, notes } = applicationReviewDialog;
    if (!application || applicationReviewDialog.submitting) return;

    setApplicationReviewDialog((prev) => ({ ...prev, submitting: true, error: "" }));

    try {
      await api.put(`/applications/${application.id}/review`, {
        status: action,
        notes: notes.trim() || null,
      });
      setAlert({ type: "success", message: `Application ${action}.` });
      closeApplicationReviewDialog();
      closeApplicationDetailDialog();
      fetchApplications();
      fetchApplicationStats();
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? `Unable to ${action} application right now.`;
      setApplicationReviewDialog((prev) => ({
        ...prev,
        submitting: false,
        error: typeof message === "string" ? message : `Unable to ${action} application right now.`,
      }));
    }
  };

  // Donation Story handlers
  const openDonationStoryDetail = (story) => {
    setDonationStoryDetailDialog({ open: true, story });
    // Mark as read when opened
    if (!story.isRead) {
      handleMarkDonationAsRead(story.id);
    }
  };

  const closeDonationStoryDetail = () => {
    setDonationStoryDetailDialog({ open: false, story: null });
  };

  const handleMarkDonationAsRead = async (id) => {
    try {
      await api.put(`/donation-stories/${id}/read`);
      setDonationStories((prev) =>
        prev.map((s) => (s.id === id ? { ...s, isRead: true } : s))
      );
      fetchDonationStats();
    } catch (err) {
      console.error("Failed to mark donation story as read:", err);
    }
  };

  const handleArchiveDonation = async (id) => {
    try {
      await api.put(`/donation-stories/${id}/archive`);
      setAlert({ type: "success", message: "Story archived." });
      closeDonationStoryDetail();
      fetchDonationStories();
      fetchDonationStats();
    } catch (err) {
      console.error("Failed to archive donation story:", err);
      setAlert({ type: "error", message: "Unable to archive story." });
    }
  };

  const handleUnarchiveDonation = async (id) => {
    try {
      await api.put(`/donation-stories/${id}/unarchive`);
      setAlert({ type: "success", message: "Story restored." });
      fetchDonationStories();
      fetchDonationStats();
    } catch (err) {
      console.error("Failed to unarchive donation story:", err);
      setAlert({ type: "error", message: "Unable to restore story." });
    }
  };

  const handleToggleArchivedDonations = () => {
    const newValue = !showArchivedDonations;
    setShowArchivedDonations(newValue);
    fetchDonationStories(newValue);
  };

  const formatApplicationDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString();
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "—";
    const dob = new Date(dateOfBirth);
    if (Number.isNaN(dob.getTime())) return "—";
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const formatApplicationValue = (value, fallback = "—") => {
    if (value === null || value === undefined) {
      return fallback;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed ? trimmed : fallback;
    }

    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : fallback;
    }

    return String(value);
  };

  const parseApplicationList = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item).trim()).filter(Boolean);
    }

    if (typeof value !== "string") {
      return [];
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      // Continue with CSV parsing below.
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const formatApplicationList = (value, labels = {}) => {
    const values = parseApplicationList(value);
    if (values.length === 0) {
      return "None";
    }

    return values
      .map((item) => labels[item] || item.replace(/_/g, " "))
      .join(", ");
  };

  const formatExperienceDetails = (value) => {
    if (!value || typeof value !== "string") {
      return [];
    }

    try {
      const parsed = JSON.parse(value);
      if (!parsed || typeof parsed !== "object") {
        return [];
      }

      return Object.entries(parsed)
        .filter(([, info]) => info && typeof info === "object")
        .map(([key, info]) => ({
          label: APPLICATION_EXPERIENCE_LABELS[key] || key.replace(/_/g, " "),
          agency: formatApplicationValue(info.agency),
          duration: formatApplicationValue(info.duration),
        }));
    } catch {
      return [];
    }
  };

  const activeApplication = applicationDetailDialog.application;

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Supervisor Actions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review and approve incident video bypass requests submitted by Rangers. Approval removes the
            incident from their missing information queue and records your decision for auditing.
          </Typography>
        </Box>

        {alert && (
          <Alert severity={alert.type} onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        <Box>
          <Typography variant="h5" gutterBottom>
            Incident Integrity Check
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Incidents exceeding 60 minutes require supervisor review before they are cleared from analytics.
          </Typography>
        </Box>

        {integrityError && (
          <Alert severity="error">{integrityError}</Alert>
        )}

        {integrityLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!integrityLoading && !integrityError && !hasIntegrityItems && (
          <Alert severity="success">All long-duration incidents have been reviewed.</Alert>
        )}

        {!integrityLoading && hasIntegrityItems && (
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Incident</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Occurred Timeline</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {integrityRows.map((incident) => {
                  const busy = Boolean(integrityBusy[incident.id]);
                  const editingCurrent = editDialog.open && editDialog.incident?.id === incident.id;
                  return (
                    <TableRow key={incident.id} hover>
                      <TableCell>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {formatIncidentType(incident.incidentType)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          CAD {incident.cadId}
                        </Typography>
                        {incident.primaryRangerId && (
                          <Typography variant="body2" color="text.secondary">
                            Primary: {incident.primaryRangerId}
                          </Typography>
                        )}
                        {incident.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Notes: {incident.notes}
                          </Typography>
                        )}
                        {incident.durationReviewApprovedAtUtc && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Approved {formatTimestamp(incident.durationReviewApprovedAtUtc)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {Number.isFinite(incident.durationMinutes) ? formatDuration(incident.durationMinutes) : "—"}
                        </Typography>
                        {Number.isFinite(incident.durationMinutes) && (
                          <Typography variant="caption" color="text.secondary">
                            {incident.durationMinutes.toFixed(1)} minutes
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">Start: {formatTimestamp(incident.occurredAtUtc)}</Typography>
                        <Typography variant="body2">
                          End: {incident.occurredEndUtc ? formatTimestamp(incident.occurredEndUtc) : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => openEditDialog(incident)}
                            disabled={busy || editDialog.open}
                          >
                            Edit Times
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleApproveDuration(incident)}
                            disabled={busy || editingCurrent}
                          >
                            Approve
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box>
          <Typography variant="h5" gutterBottom>
            Pending Bypass Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Requests awaiting supervisor review.
          </Typography>
        </Box>

        {pendingError && (
          <Alert severity="error">{pendingError}</Alert>
        )}

        {loadingPending && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loadingPending && !hasRequests && !pendingError && (
          <Alert severity="success">No pending bypass approvals. Great job keeping the queue clear.</Alert>
        )}

        {!loadingPending && hasRequests && (
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Incident</TableCell>
                  <TableCell>Reason Provided</TableCell>
                  <TableCell>Submitted By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRequests.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {formatIncidentType(item.incidentType)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        CAD {item.cadId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Occurred {formatTimestamp(item.occurredAtUtc)}
                      </Typography>
                      {item.primaryRangerId && (
                        <Typography variant="body2" color="text.secondary">
                          Primary: {item.primaryRangerId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2">{item.reason}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.requestedByUsername || item.requestedByRangerId || "Unknown"}
                      </Typography>
                      {item.requestedByRangerId && (
                        <Typography variant="caption" color="text.secondary">
                          Ranger ID: {item.requestedByRangerId}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Submitted {formatTimestamp(item.requestedAtUtc)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button variant="contained" size="small" onClick={() => openApproveConfirmation(item)}>
                        Approve Bypass
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box>
          <Typography variant="h5" gutterBottom>
            Previously Approved Bypasses
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {approvalHistory ? "Approval history, 20 at a time." : "Up to 10 approvals from the last 30 days."}
          </Typography>
        </Box>

        {approvedError && (
          <Alert severity="error">{approvedError}</Alert>
        )}

        {loadingApproved && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loadingApproved && !hasApprovedLogs && !approvedError && (
          <Alert severity="info">{approvalHistory ? "No more approvals found." : "No approvals in the last 30 days. Use View older approvals for history."}</Alert>
        )}

        {!loadingApproved && hasApprovedLogs && (
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Incident</TableCell>
                  <TableCell>Reason Provided</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Approved By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approvedLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {formatIncidentType(log.incidentType)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        CAD {log.cadId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Occurred {formatTimestamp(log.occurredAtUtc)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2">{log.reason}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {log.requestedByUsername || log.requestedByRangerId || "Unknown"}
                      </Typography>
                      {log.requestedByRangerId && (
                        <Typography variant="caption" color="text.secondary">
                          Ranger ID: {log.requestedByRangerId}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Submitted {formatTimestamp(log.requestedAtUtc)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {log.approvedByUsername || log.approvedByRangerId || "Unknown"}
                      </Typography>
                      {log.approvedByRangerId && (
                        <Typography variant="caption" color="text.secondary">
                          Ranger ID: {log.approvedByRangerId}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Approved {formatTimestamp(log.approvedAtUtc)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Stack direction="row" spacing={1}>
          <Button disabled={loadingApproved} onClick={() => fetchApprovedLogs(!approvalHistory)}>{approvalHistory ? 'Show recent approvals' : 'View older approvals'}</Button>
          {approvalHistory && approvalMore && <Button disabled={loadingApproved} onClick={() => fetchApprovedLogs(true, Math.min(...approvedLogs.map(log => log.id)))}>Load more approvals</Button>}
        </Stack>

        {/* Applications Section */}
        <Divider sx={{ my: 2 }} />
        
        <Box>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <PersonAddIcon color="primary" />
            <Typography variant="h5">
              Membership Applications
            </Typography>
            {applicationStats.pending > 0 && (
              <Chip 
                label={`${applicationStats.pending} pending`} 
                color="warning" 
                size="small" 
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review applications from prospective members. Total: {applicationStats.total} ({applicationStats.approved} approved, {applicationStats.denied} denied)
          </Typography>
        </Box>

        <Tabs value={applicationFilter} onChange={handleApplicationFilterChange} sx={{ mb: 2 }}>
          <Tab value="pending" label={`Pending (${applicationStats.pending})`} />
          <Tab value="approved" label={`Approved (${applicationStats.approved})`} />
          <Tab value="denied" label={`Denied (${applicationStats.denied})`} />
          <Tab value="all" label="All" />
        </Tabs>

        {applicationsError && (
          <Alert severity="error">{applicationsError}</Alert>
        )}

        {applicationsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!applicationsLoading && !hasApplications && !applicationsError && (
          <Alert severity="info">
            {applicationFilter === "pending" 
              ? "No pending applications to review." 
              : `No ${applicationFilter} applications found.`}
          </Alert>
        )}

        {!applicationsLoading && hasApplications && (
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {app.fullName || `${app.firstName} ${app.lastName}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        DOB: {formatApplicationDate(app.dateOfBirth)} (Age: {calculateAge(app.dateOfBirth)})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{app.city || "—"}, {app.state || "TX"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.primaryAvailabilityArea || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{app.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.phone}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTimestamp(app.submittedAtUtc)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={app.status} 
                        size="small"
                        color={app.status === "approved" ? "success" : app.status === "denied" ? "error" : "warning"}
                      />
                      {app.reviewedAtUtc && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatApplicationDate(app.reviewedAtUtc)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => openApplicationDetail(app)}
                        >
                          View
                        </Button>
                        {app.status === "pending" && (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckIcon />}
                              onClick={() => openApplicationReview(app, "approved")}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<CloseIcon />}
                              onClick={() => openApplicationReview(app, "denied")}
                            >
                              Deny
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Donation Stories Section */}
        <Divider sx={{ my: 2 }} />
        
        <Box>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <FavoriteIcon color="error" />
            <Typography variant="h5">
              Donations Received
            </Typography>
            {donationStats.unread > 0 && (
              <Chip 
                label={`${donationStats.unread} unread`} 
                color="error" 
                size="small" 
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Stories shared by donors who supported our mission. Total: {donationStats.total} ({donationStats.archived} archived)
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant={showArchivedDonations ? "outlined" : "contained"}
            size="small"
            onClick={handleToggleArchivedDonations}
          >
            {showArchivedDonations ? "Hide Archived" : "Show All (incl. Archived)"}
          </Button>
        </Stack>

        {donationStoriesError && (
          <Alert severity="error">{donationStoriesError}</Alert>
        )}

        {donationStoriesLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!donationStoriesLoading && !hasDonationStories && !donationStoriesError && (
          <Alert severity="info">
            No donation stories have been submitted yet.
          </Alert>
        )}

        {!donationStoriesLoading && hasDonationStories && (
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>From</TableCell>
                  <TableCell>Story Preview</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {donationStories.map((story) => (
                  <TableRow 
                    key={story.id} 
                    hover
                    sx={{ 
                      bgcolor: !story.isRead ? "rgba(25, 118, 210, 0.08)" : "inherit",
                      opacity: story.isArchived ? 0.6 : 1,
                    }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: story.isRead ? 400 : 700 }}>
                        {story.name || "Anonymous"}
                      </Typography>
                      {story.email && (
                        <Typography variant="caption" color="text.secondary">
                          {story.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          overflow: "hidden", 
                          textOverflow: "ellipsis", 
                          whiteSpace: "nowrap",
                          fontWeight: story.isRead ? 400 : 600,
                        }}
                      >
                        {story.story}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTimestamp(story.submittedAtUtc)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {!story.isRead && (
                          <Chip label="New" size="small" color="primary" />
                        )}
                        {story.isArchived && (
                          <Chip label="Archived" size="small" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => openDonationStoryDetail(story)}
                        >
                          Read
                        </Button>
                        {story.isArchived ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleUnarchiveDonation(story.id)}
                          >
                            Restore
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            onClick={() => handleArchiveDonation(story.id)}
                          >
                            Archive
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <Dialog open={editDialog.open} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Incident Call Times</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {editDialog.incident && (
            <>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                CAD {editDialog.incident.cadId} — {formatIncidentType(editDialog.incident.incidentType)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current duration {typeof currentIncidentDuration === "number" && Number.isFinite(currentIncidentDuration) ? formatDuration(currentIncidentDuration) : "unknown"}.
              </Typography>
            </>
          )}

          <TextField
            label="Start (Occurred At)"
            type="datetime-local"
            value={editDialog.start}
            onChange={handleEditFieldChange("start")}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            label="End (Occurred End)"
            type="datetime-local"
            value={editDialog.end}
            onChange={handleEditFieldChange("end")}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          {typeof proposedDuration === "number" && Number.isFinite(proposedDuration) && (
            <Typography variant="body2" color="text.secondary">
              Proposed duration {formatDuration(proposedDuration)} ({proposedDuration.toFixed(1)} minutes).
            </Typography>
          )}

          {editDialog.error && (
            <Alert severity="error">{editDialog.error}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={editDialog.submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editDialog.submitting}
          >
            {editDialog.submitting ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={approveDialog.open} onClose={closeApproveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Video Bypass</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {approveDialog.request && (
            <>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                CAD {approveDialog.request.cadId} — {formatIncidentType(approveDialog.request.incidentType)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Submitted by {approveDialog.request.requestedByUsername || approveDialog.request.requestedByRangerId || "Unknown"}
                
                on {formatTimestamp(approveDialog.request.requestedAtUtc)}.
              </Typography>
              <Typography variant="subtitle2">Reason Provided</Typography>
              <Typography variant="body2">{approveDialog.request.reason}</Typography>
            </>
          )}

          {approveDialog.error && (
            <Alert severity="error">{approveDialog.error}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApproveDialog} disabled={approveDialog.submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            disabled={approveDialog.submitting}
          >
            {approveDialog.submitting ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Application Detail Dialog */}
      <Dialog 
        open={applicationDetailDialog.open} 
        onClose={closeApplicationDetailDialog} 
        maxWidth="lg" 
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h6">Membership Application</Typography>
              {activeApplication && (
                <Typography variant="body2" color="text.secondary">
                  {formatApplicationValue(activeApplication.fullName, `${formatApplicationValue(activeApplication.firstName, "")} ${formatApplicationValue(activeApplication.lastName, "")}`.trim() || "Unknown Applicant")}
                </Typography>
              )}
            </Box>
            {activeApplication?.status && (
              <Chip 
                label={activeApplication.status} 
                color={activeApplication.status === "approved" ? "success" : activeApplication.status === "denied" ? "error" : "warning"}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {applicationDetailDialog.loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {activeApplication && !applicationDetailDialog.loading && (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
                  Applicant Summary
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mt: 0.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Applicant</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatApplicationValue(activeApplication.fullName, `${formatApplicationValue(activeApplication.firstName, "")} ${formatApplicationValue(activeApplication.lastName, "")}`.trim() || "Unknown Applicant")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Submitted</Typography>
                    <Typography variant="body2">{formatTimestamp(activeApplication.submittedAtUtc)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.email)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.phone)}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  SECTION 1: BASIC INFORMATION
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">First Name</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.firstName)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Last Name</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.lastName)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                    <Typography variant="body2">
                      {formatApplicationDate(activeApplication.dateOfBirth)}
                      {activeApplication.dateOfBirth ? ` (Age: ${calculateAge(activeApplication.dateOfBirth)})` : ""}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">City</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.city)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">State</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.state, "TX")}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">County / Primary Availability Area</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.primaryAvailabilityArea)}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  SECTION 2: EXPERIENCE & TRAINING
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Experience</Typography>
                    <Typography variant="body2">
                      {formatApplicationList(activeApplication.experience, APPLICATION_EXPERIENCE_LABELS)}
                    </Typography>
                  </Box>

                  {formatExperienceDetails(activeApplication.experienceDetails).length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Experience Details</Typography>
                      <Paper variant="outlined" sx={{ mt: 0.75, p: 1.5, bgcolor: "action.hover" }}>
                        <Stack spacing={1}>
                          {formatExperienceDetails(activeApplication.experienceDetails).map((detail) => (
                            <Typography key={detail.label} variant="body2">
                              <strong>{detail.label}:</strong> {detail.agency} ({detail.duration})
                            </Typography>
                          ))}
                        </Stack>
                      </Paper>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="caption" color="text.secondary">Certifications</Typography>
                    <Typography variant="body2">
                      {formatApplicationList(activeApplication.certifications, APPLICATION_CERTIFICATION_LABELS)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Other Certifications</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.otherCertifications, "None")}</Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  SECTION 3: VEHICLE & EQUIPMENT
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Vehicle Year</Typography>
                      <Typography variant="body2">{formatApplicationValue(activeApplication.vehicleYear)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Vehicle Make</Typography>
                      <Typography variant="body2">{formatApplicationValue(activeApplication.vehicleMake)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Vehicle Model</Typography>
                      <Typography variant="body2">{formatApplicationValue(activeApplication.vehicleModel)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Vehicle Type</Typography>
                      <Typography variant="body2">
                        {APPLICATION_VEHICLE_TYPE_LABELS[activeApplication.vehicleType] || formatApplicationValue(activeApplication.vehicleType)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Equipment Owned</Typography>
                    <Typography variant="body2">
                      {formatApplicationList(activeApplication.equipment, APPLICATION_EQUIPMENT_LABELS)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Emergency Warning Lights Details</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.emergencyWarningLightsDetails, "None")}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Other Equipment</Typography>
                    <Typography variant="body2">{formatApplicationValue(activeApplication.otherEquipment, "None")}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Roadside Assistance Capabilities</Typography>
                    <Box sx={{ mt: 0.75, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
                      <Typography variant="body2">Tire changes: {activeApplication.canPerformTireChanges ? "Yes" : "No"}</Typography>
                      <Typography variant="body2">Push/Pull assistance: {activeApplication.canPerformPushPull ? "Yes" : "No"}</Typography>
                      <Typography variant="body2">Fuel delivery: {activeApplication.canDeliverFuel ? "Yes" : "No"}</Typography>
                      <Typography variant="body2">Light mechanic work: {activeApplication.canPerformLightMechanic ? "Yes" : "No"}</Typography>
                      <Typography variant="body2">Major mechanic work: {activeApplication.canPerformMajorMechanic ? "Yes" : "No"}</Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  SECTION 4: AVAILABILITY
                </Typography>
                <Typography variant="caption" color="text.secondary">How often could you be available to assist?</Typography>
                <Typography variant="body2">
                  {APPLICATION_FREQUENCY_LABELS[activeApplication.availabilityFrequency] || formatApplicationValue(activeApplication.availabilityFrequency, "Not specified")}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  SECTION 5: SITUATIONAL QUESTIONS
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Scenario 1</Typography>
                    <Typography variant="caption" color="text.secondary">
                      You come across a stranded motorist who appears frustrated and upset. How would you approach the situation?
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 0.75, bgcolor: "action.hover" }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{formatApplicationValue(activeApplication.scenarioStranded)}</Typography>
                    </Paper>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Scenario 2</Typography>
                    <Typography variant="caption" color="text.secondary">
                      While assisting a motorist, a police officer stops and begins questioning your authority to help or criticizes your methods. How do you handle this?
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 0.75, bgcolor: "action.hover" }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{formatApplicationValue(activeApplication.scenarioConflict)}</Typography>
                    </Paper>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Scenario 3</Typography>
                    <Typography variant="caption" color="text.secondary">
                      You encounter a situation that is beyond your skill level or equipment capabilities. What do you do?
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 0.75, bgcolor: "action.hover" }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{formatApplicationValue(activeApplication.scenarioLimitations)}</Typography>
                    </Paper>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Scenario 4</Typography>
                    <Typography variant="caption" color="text.secondary">
                      What would you do if you arrived first at a crash scene?
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 0.75, bgcolor: "action.hover" }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{formatApplicationValue(activeApplication.scenarioCrashScene)}</Typography>
                    </Paper>
                  </Box>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  SECTION 6: PERSONAL STATEMENT
                </Typography>
                <Typography variant="caption" color="text.secondary">Why do you want to join Texas Highway Rangers?</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.75, bgcolor: "action.hover" }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{formatApplicationValue(activeApplication.whyJoin)}</Typography>
                </Paper>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  SECTION 7: VALUES & ACKNOWLEDGEMENTS
                </Typography>
                <Stack spacing={0.75}>
                  <Typography variant="body2">Agrees to values and professional conduct: {activeApplication.agreesToValues ? "Yes" : "No"}</Typography>
                  <Typography variant="body2">Understands training requirements: {activeApplication.agreesToTraining ? "Yes" : "No"}</Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: activeApplication.acknowledgedNoAI ? "success.main" : "error.main" }}
                  >
                    Certified NO AI-generated responses: {activeApplication.acknowledgedNoAI ? "Yes" : "No"}
                  </Typography>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.5 }}>
                  SECTION 8: SIGNATURE
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Typed Signature</Typography>
                    <Typography variant="body2" sx={{ fontStyle: "italic" }}>{formatApplicationValue(activeApplication.typedSignature)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Signature Date</Typography>
                    <Typography variant="body2">{formatApplicationDate(activeApplication.signatureDate)}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Submitted: {formatTimestamp(activeApplication.submittedAtUtc)}
                  {activeApplication.ipAddress && ` • IP: ${activeApplication.ipAddress}`}
                </Typography>
                {activeApplication.reviewedAtUtc && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Reviewed: {formatTimestamp(activeApplication.reviewedAtUtc)}
                    {activeApplication.reviewNotes && ` • Notes: ${activeApplication.reviewNotes}`}
                  </Typography>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApplicationDetailDialog}>Close</Button>
          {activeApplication?.status === "pending" && (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => openApplicationReview(activeApplication, "denied")}
              >
                Deny
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => openApplicationReview(activeApplication, "approved")}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Application Review Confirmation Dialog */}
      <Dialog open={applicationReviewDialog.open} onClose={closeApplicationReviewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {applicationReviewDialog.action === "approved" ? "Approve Application" : "Deny Application"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {applicationReviewDialog.application && (
            <Typography variant="body1">
              Are you sure you want to <strong>{applicationReviewDialog.action === "approved" ? "approve" : "deny"}</strong> the 
              application from <strong>{applicationReviewDialog.application.fullName || `${applicationReviewDialog.application.firstName} ${applicationReviewDialog.application.lastName}`}</strong>?
            </Typography>
          )}

          <TextField
            label="Review Notes (optional)"
            multiline
            rows={3}
            value={applicationReviewDialog.notes}
            onChange={(e) => setApplicationReviewDialog((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Add any notes about your decision..."
            fullWidth
          />

          {applicationReviewDialog.error && (
            <Alert severity="error">{applicationReviewDialog.error}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApplicationReviewDialog} disabled={applicationReviewDialog.submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleApplicationReview}
            variant="contained"
            color={applicationReviewDialog.action === "approved" ? "success" : "error"}
            disabled={applicationReviewDialog.submitting}
          >
            {applicationReviewDialog.submitting 
              ? (applicationReviewDialog.action === "approved" ? "Approving..." : "Denying...") 
              : (applicationReviewDialog.action === "approved" ? "Approve" : "Deny")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Donation Story Detail Dialog */}
      <Dialog 
        open={donationStoryDetailDialog.open} 
        onClose={closeDonationStoryDetail} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FavoriteIcon color="error" />
            <Typography variant="h6">Donation Story</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {donationStoryDetailDialog.story && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">From</Typography>
                <Typography variant="body1">
                  {donationStoryDetailDialog.story.name || "Anonymous"}
                </Typography>
              </Box>
              
              {donationStoryDetailDialog.story.email && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">
                    {donationStoryDetailDialog.story.email}
                  </Typography>
                </Box>
              )}
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Submitted</Typography>
                <Typography variant="body1">
                  {formatTimestamp(donationStoryDetailDialog.story.submittedAtUtc)}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Their Story</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: "grey.50" }}>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {donationStoryDetailDialog.story.story}
                  </Typography>
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {donationStoryDetailDialog.story && !donationStoryDetailDialog.story.isArchived && (
            <Button 
              onClick={() => handleArchiveDonation(donationStoryDetailDialog.story.id)}
              color="warning"
            >
              Archive
            </Button>
          )}
          {donationStoryDetailDialog.story && donationStoryDetailDialog.story.isArchived && (
            <Button 
              onClick={() => handleUnarchiveDonation(donationStoryDetailDialog.story.id)}
            >
              Restore
            </Button>
          )}
          <Button onClick={closeDonationStoryDetail} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Supervisor;
